import { z } from 'zod';
import { apiClient } from '@api/client';
import type { ApiResponseShape, CommonResponse } from '@/types/api';
import {
  priceSeriesSchema,
  type ListPriceSeriesParams,
  type PriceSeries,
} from './schema';

const BASE_PATH = '/price-series';

const listResponseSchema = z.object({
  message: z.string(),
  columnDefs: z.array(z.record(z.unknown())).optional(),
  rowData: z.array(priceSeriesSchema),
  gridOptions: z.record(z.unknown()).optional(),
  paginationDetails: z
    .object({
      page: z.number(),
      pageSize: z.number(),
      totalRecords: z.number(),
      isLastPage: z.boolean(),
    })
    .optional(),
});

const recordResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: priceSeriesSchema.nullable(),
  error: z.string().nullable().optional(),
});

// Replace-on-present payload — only fields the editor touched make it across.
// `notes` may be sent as the empty string to clear it (server normalizes).
export interface UpdatePriceSeriesPayload {
  isActive?: boolean;
  isManualMarketPrice?: boolean;
  runPredictionPipeline?: boolean;
  showPredictionInApp?: boolean;
  notes?: string | null;
}

function pruneParams(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v;
  }
  return out;
}

export async function listPriceSeries(
  params: ListPriceSeriesParams,
): Promise<CommonResponse<PriceSeries>> {
  const response = await apiClient.get(BASE_PATH, {
    params: pruneParams(params as unknown as Record<string, unknown>),
  });
  const parsed = listResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new Error(`Invalid list response: ${parsed.error.message}`);
  }
  return parsed.data;
}

export async function updatePriceSeries(
  id: string,
  payload: UpdatePriceSeriesPayload,
): Promise<PriceSeries> {
  const response = await apiClient.put<ApiResponseShape<PriceSeries>>(
    `${BASE_PATH}/${id}`,
    payload,
  );
  const parsed = recordResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty update response' : parsed.error.message);
  }
  return parsed.data.data;
}
